import React, { useState } from 'react';
import { 
    PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
    LineChart, Line, XAxis, YAxis, Legend, ResponsiveContainer, 
    CartesianGrid, Tooltip, PieChart, Pie, Sector, Cell, Text as ReText, RadarChart, BarChart, Bar, Label, LabelList 
} from 'recharts';
import { View } from 'react-native';
import { Text } from '../../../globalComponents/NativeOverrides';
import Colors from '../../../constants/Colorsv2';
import { formatLineChart } from '../AnalyticUtils/chartHelpers';
import { ProgressDataType } from '../../../objectTypes';
import { PublicPlayerProps } from '../../../store/types/auth_svc';
import { PremiumAnalytics } from '../../../Componentsv1/Icons';

export type LineDataType = { name:string, data: { label: string, amt:number, color:string }[] }
type AnalyticLineChartProps = { data: LineDataType[], width:number, tick_prefix?:string, tick_suffix?:string }
export const AnalyticLineChart = ({ data, width, tick_prefix, tick_suffix }:AnalyticLineChartProps) => {

    const { lineData, dataKeys } = formatLineChart(data)
    return (
        <ResponsiveContainer width={width} height={width/1.6}>
             <LineChart 
             width={width} 
             height={width/1.5} 
             data={lineData}
             margin={{
                top: 5,
                right: 5,
                left: 5,
                bottom: 5,
              }}>
                <CartesianGrid vertical={false}/>
                <XAxis dy={10} height={50} dataKey='name' axisLine={false} tickLine={false} interval='preserveStartEnd' tick={{ fill: Colors.brand.slate, fontSize:10, fontFamily:'barlow-regular' }}/>
                <YAxis orientation='left' axisLine={false} tickLine={false} interval='preserveStartEnd' tick={{ fill: Colors.brand.slate, fontSize:10, fontFamily:'barlow-regular' }} tickFormatter={(value) => `${tick_prefix??''}${value.toFixed(2)}${tick_suffix??''}`} />
                <Legend verticalAlign='top' wrapperStyle={{ paddingBottom:20, fontFamily:'barlow-regular', fontSize:12 }}/>
                <Tooltip content={<CustomTooltip />}/>
                {dataKeys.map(d => {
                    return (
                        <Line type='monotone' dot={false}  dataKey={d.key} stroke={d.color} strokeWidth={2} activeDot={{ r: 8, onClick: () => console.log('CLicked!!!') }}/>
                    )
                })}
            </LineChart>
        </ResponsiveContainer>
    )
}

const renderActiveShape = (props:any) => {
    const RADIAN = Math.PI / 180;
    const { cx, cy, midAngle, innerRadius, outerRadius, startAngle, endAngle, fill, payload, percent, value } = props;
    const sin = Math.sin(-RADIAN * midAngle);
    const cos = Math.cos(-RADIAN * midAngle);
    const sx = cx + (outerRadius + 10) * cos;
    const sy = cy + (outerRadius + 10) * sin;
    const mx = cx + (outerRadius + 30) * cos;
    const my = cy + (outerRadius + 30) * sin;
    const ex = mx + (cos >= 0 ? 1 : -1) * 22;
    const ey = my;
    const textAnchor = cos >= 0 ? 'start' : 'end';
  
    return (
      <g>
        <ReText x={cx} y={cy} dy={8} width={cy-10} fontSize={12} textAnchor='middle' verticalAnchor='top' fill={fill}>
            {`${payload.name} ${payload.value.toFixed(2)}`}
        </ReText>
        
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
        />
      </g>
    );
  };

  /*
  <Sector
          cx={cx}
          cy={cy}
          startAngle={startAngle}
          endAngle={endAngle}
          innerRadius={outerRadius + 6}
          outerRadius={outerRadius + 10}
          fill={fill}
        />
  */

export const AnalyticPieChart = ({ data, width }) => {
    const [ activeIndex, setActiveIndex ] = useState(0)
    
    return (
        <ResponsiveContainer height="100%" width="100%">
            <PieChart width={width} height={width}>
            <Pie
                activeIndex={activeIndex}
                activeShape={renderActiveShape}
                data={data}
                label
                cx={'50%'}
                cy={'50%'}
                innerRadius={width*0.3}
                outerRadius={width*0.4}
                fill="#8884d8"
                dataKey="value"
                onMouseEnter={(props:any, index:number) => setActiveIndex(index)}
            >
                {
                    data.map((entry:any, index:number) => <Cell fill={entry.color}/>)
                }
                </Pie>
                <Legend wrapperStyle={{ paddingTop:20, fontFamily:'barlow-regular', fontSize:12, marginTop:width }}/>
            </PieChart>
        </ResponsiveContainer>
    )
}


const radarData = [
    {
      subject: 'Math',
      A: 120,
      B: 110,
      fullMark: 150,
    },
    {
      subject: 'Chinese',
      A: 98,
      B: 130,
      fullMark: 75,
    },
    {
      subject: 'English',
      A: 86,
      B: 130,
      fullMark: 150,
    },
    {
      subject: 'Geography',
      A: 99,
      B: 100,
      fullMark: 150,
    },
    {
      subject: 'Physics',
      A: 85,
      B: 90,
      fullMark: 150,
    },
    {
      subject: 'History',
      A: 65,
      B: 85,
      fullMark: 150,
    },
  ];

export const AnalyticRadarChart = ({ data, width }) => {

    return (
        <ResponsiveContainer width={width} height={width}>
            <RadarChart outerRadius={90} width={730} height={250} data={radarData}>
                <PolarGrid />
                <PolarAngleAxis dataKey="subject" />
                <PolarRadiusAxis angle={30} domain={[0, 150]} />
                <Radar name="Mike" dataKey="A" stroke="#8884d8" fill="#8884d8" fillOpacity={0.6} />
                <Radar name="Lily" dataKey="B" stroke="#82ca9d" fill="#82ca9d" fillOpacity={0.6} />
                <Legend />
            </RadarChart>
        </ResponsiveContainer>
    )
}



const CustomizedLabel = (props:any) => {
  const {x, y, stroke, value, width, color } = props;
  return (
    <text x={x + (width/2)} y={y-6} textAnchor='middle' fill={color} fontFamily='barlo-regular' fontSize={12}>{value.toFixed(2)}</text>
  )
}

const CustomTooltip = (props:any) => {
  const { payload } = props;
  if(!payload){ return (<></>) }
  return (
    <View style={{ borderRadius: 8, backgroundColor:Colors.brand.midnight, padding:10 }}>
      {payload.map(p => {
        return (
          <View style={{flexDirection:'row', alignItems:'center', marginBottom:3}}>
            <Text size={12} color={Colors.shades.shade400} weight='regular'>{p.name}</Text>
            <Text size={12} color={Colors.shades.white} weight='semibold' style={{ marginLeft:8 }}>{p.value?.toFixed(2)??0}</Text>
          </View>
        )
      })}
      
    </View>
  )
}

export const AnalyticBarChart = ({ chart_data, width }) => {
  let data:any = []
  chart_data.map(d => {
    let obj = { name: d.name, amt: d.data[0].amt, color:d.data[0].color }
    data.push(obj)
  })

  return (
    <ResponsiveContainer width="100%" height="100%">
            <BarChart barCategoryGap='10%' width={width} height={width} data={data}>
            <CartesianGrid vertical={false} />
            <Tooltip content={<CustomTooltip />}/>
            <Bar isAnimationActive={false} dataKey='amt' label={<CustomizedLabel />}>
            {
              data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
              ))
            }
            </Bar>
            <XAxis axisLine={false} tickLine={false} dataKey="name" interval={'preserveStartEnd'} tick={{ fill: Colors.brand.slate, fontSize:10, fontFamily:'barlow-regular' }}/>
            <YAxis axisLine={false} tickLine={false}  tick={{ fill: Colors.brand.slate, fontSize:10, fontFamily:'barlow-regular' }} tickFormatter={(value) => `$${value.toFixed(2)}`}/>
          </BarChart>
        </ResponsiveContainer>
  )
}


type AnalyticProgressChartProps = { chart_data:ProgressDataType[], width:number }
export const AnalyticProgressChart = ({ chart_data, width } : AnalyticProgressChartProps) => {
  const calcLeftWidth = (data:ProgressDataType) => {
    const total_value = data.left_value + data.right_value
    const leftWidth = data.left_value / total_value * width
    const rightWidth = width - leftWidth
    const left_pct = data.left_value / total_value * 100
    const right_pct = data.right_value / total_value * 100
    return { leftWidth, rightWidth, left_pct, right_pct }
  }

  return (
    <View>
        {chart_data.map(d => {
          const { leftWidth, rightWidth, left_pct, right_pct } = calcLeftWidth(d)
          return (
            <View style={{margin:5}}>
              <View style={{flexDirection:'row', alignItems:'center'}}>
                <Text style={{ flex:1 }} size={14} color={Colors.brand.cobalt} textAlign='left'>{left_pct.toFixed()}%</Text>
                <Text style={{ flex:1 }} size={14} color={Colors.brand.midnight} textAlign='center'>{d.name}</Text>
                <Text style={{ flex:1 }} size={14} color={Colors.brand.cobalt} textAlign='right'>{right_pct.toFixed()}%</Text>
              </View>
              <View style={{ flexDirection:'row', alignItems:'center', marginTop:4, marginBottom:4 }}>
                <View style={{ borderTopLeftRadius:100, marginRight:1, height:12, borderBottomLeftRadius:100, width: leftWidth, backgroundColor:Colors.highlights.highlight300 }} />
                <View style={{ borderTopRightRadius:100, marginLeft:1, height:12, borderBottomRightRadius:100, width: rightWidth, backgroundColor:Colors.utility.success }} />
              </View>
              <View style={{flexDirection:'row', alignItems:'center'}}>
                <Text style={{ flex:1 }} size={10} color={Colors.highlights.highlight300} textAlign='left'>Lost</Text>
                <Text style={{ flex:1 }} size={10} color={Colors.utility.success} textAlign='right'>Won</Text>
              </View>
            </View>
          )
        })}
    </View>
  )
}